import * as React from "react";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DialogContentProps } from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

// Radix unmounts once animationend fires. 200ms, strong ease-out.
// The panel stays centered (translate) and grows from 0.95. Reduced motion fades only.
const dialogMotion = `
@keyframes dialog-panel-in {
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
}
@keyframes dialog-panel-out {
  to { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
}
@keyframes dialog-fade-in {
  from { opacity: 0; }
}
@keyframes dialog-fade-out {
  to { opacity: 0; }
}
[data-slot="dialog-panel"] {
  transform: translate(-50%, -50%);
  transform-origin: center;
}
[data-slot="dialog-panel"][data-state="open"] {
  animation: dialog-panel-in 200ms cubic-bezier(0.23, 1, 0.32, 1);
}
[data-slot="dialog-panel"][data-state="closed"] {
  animation: dialog-panel-out 200ms cubic-bezier(0.23, 1, 0.32, 1);
}
[data-slot="dialog-overlay"][data-state="open"] {
  animation: dialog-fade-in 200ms cubic-bezier(0.23, 1, 0.32, 1);
}
[data-slot="dialog-overlay"][data-state="closed"] {
  animation: dialog-fade-out 200ms cubic-bezier(0.23, 1, 0.32, 1);
}
@media (prefers-reduced-motion: reduce) {
  [data-slot="dialog-panel"][data-state="open"] {
    animation-name: dialog-fade-in;
  }
  [data-slot="dialog-panel"][data-state="closed"] {
    animation-name: dialog-fade-out;
  }
}
`;

function Dialog({ ...props }: Readonly<DialogPrimitive.DialogProps>) {
  React.useEffect(() => {
    const id = "dialog-motion";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = dialogMotion;
    document.head.appendChild(style);
  }, []);

  return <DialogPrimitive.Root {...props} />;
}

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = ({ ...props }: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
);
DialogPortal.displayName = DialogPrimitive.Portal.displayName;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="dialog-overlay"
    className={cn(
      "bg-background/80 fixed inset-0 z-50 overflow-hidden backdrop-blur-xs",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface IDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  nonDismissable?: boolean;
}
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  IDialogContentProps
>(
  (
    {
      className,
      children,
      nonDismissable = false,
      onInteractOutside,
      ...props
    },
    ref
  ) => {
    const handleInteractOutside: DialogContentProps["onInteractOutside"] =
      e => {
        if (nonDismissable) {
          e.preventDefault();
        }
        onInteractOutside?.(e);
      };

    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          data-slot="dialog-panel"
          className={cn(
            "bg-background fixed top-[50%] left-[50%] z-50 grid w-full max-w-lg gap-4 rounded-md border p-6 shadow-lg sm:rounded-lg md:w-full",
            "ring-primary ring-offset-background focus-visible:ring-ring transition-colors focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:outline-hidden",
            className
          )}
          onInteractOutside={handleInteractOutside}
          {...props}
        >
          {children}
          {!nonDismissable && (
            <DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);

DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg leading-none font-semibold tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

Dialog.Trigger = DialogTrigger;
Dialog.Content = DialogContent;
Dialog.Header = DialogHeader;
Dialog.Footer = DialogFooter;
Dialog.Title = DialogTitle;
Dialog.Description = DialogDescription;

export {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
};
